<template>
  <aside
    class="flex flex-col gap-0.5 shrink-0 w-full lg:w-61.5 p-5 lg:px-4.5
    bg-background border-b lg:border-b-0 lg:border-r border-border"
  >
    <h3 class="mb-2 text-xs font-bold uppercase tracking-ds-caps text-muted-foreground">
      Steps
    </h3>

    <!--
      A map and a way in, not a gate: every step is reachable at any time, and a
      count rather than a step number, because what matters is where the gaps
      are rather than how far through the user is. What stops a create is an
      unfilled required field, never where they are standing.

      Reka's Stepper, so the rail is a real one: arrow keys move between steps,
      each trigger is labelled by its own title, and a polite live region
      announces the move. `linear=false` is the "every step reachable" rule
      stated where Reka can enforce it — the default would disable everything
      past the next step. `aria-current` is ours: Reka sets data-state, which is
      for styling, and says nothing to a screen reader about where you are.
    -->
    <DSStepper
      :model-value="activeStep"
      orientation="vertical"
      :linear="false"
      class="gap-0.5"
      @update:model-value="handleSelect"
    >
      <DSStepperItem
        v-for="(step, index) in steps"
        :key="step.key"
        class="w-full"
        :step="index + 1"
        :completed="step.state === WizardStepState.COMPLETE"
      >
        <DSStepperTrigger
          class="flex-row w-full gap-2.5 px-2.5 py-1.5 text-left"
          :class="step.key === activeKey
            ? 'bg-card border border-accent'
            : 'border border-transparent hover:bg-secondary'"
          :aria-current="step.key === activeKey ? 'step' : undefined"
        >
          <ListChecks
            v-if="step.key === activeKey && step.key === SUMMARY_STEP_KEY"
            class="size-3.5 shrink-0"
          />
          <Check
            v-else-if="step.state === WizardStepState.COMPLETE"
            class="size-3.5 shrink-0 text-success"
          />
          <span
            v-else-if="step.key === activeKey"
            class="size-1.5 mx-1 shrink-0 rounded-full bg-accent-foreground"
          />
          <Minus
            v-else-if="step.state === WizardStepState.SKIPPED
              || step.state === WizardStepState.OPTIONAL"
            class="size-3.5 shrink-0 text-muted-foreground"
          />
          <span
            v-else
            class="size-1.5 mx-1 shrink-0 rounded-full bg-warning"
          />

          <!--
            `as="span"`: StepperTitle is an <h4> by default, which would put one
            heading per step into the document outline for what is a list.
          -->
          <DSStepperTitle
            as="span"
            class="flex-1 min-w-0 truncate font-medium"
            :class="step.key === activeKey ? 'font-bold text-accent-foreground' : 'text-foreground'"
          >
            {{ step.label }}
          </DSStepperTitle>

          <!--
            Which of the two empties this is: a step the user opened and left
            has been skipped, and one they have never opened is still just
            optional. Saying "skipped" about somewhere nobody has been reads as
            the wizard having decided something on their behalf.
          -->
          <span
            v-if="step.state === WizardStepState.SKIPPED
              || step.state === WizardStepState.OPTIONAL"
            class="text-xs font-medium text-muted-foreground"
          >
            {{ step.state === WizardStepState.OPTIONAL ? 'optional' : 'skipped' }}
          </span>
          <span
            v-else-if="step.count"
            class="text-xs font-bold text-warning-subtle-foreground"
          >
            {{ step.count }}
          </span>
        </DSStepperTrigger>
      </DSStepperItem>
    </DSStepper>

    <!--
      The legend is load-bearing, not decoration: it is the only place the three
      provenance marks are named, and the spec's promise is that a reader can
      always tell what the wizard read from what it is asking for.

      A list rather than the <dl> this used to be: a definition list needs a
      <dt> per <dd>, and the term here is a glyph with no text of its own, so
      the markup was invalid and read as values with no terms.
    -->
    <ul class="flex flex-col gap-2 mt-4 pt-3.5 border-t border-border text-xs font-medium leading-normal text-muted-foreground">
      <li
        v-if="hasSentence"
        class="flex gap-2"
      >
        <Sparkles class="size-3 shrink-0 mt-0.5 text-brand" />
        {{ FIELD_ORIGIN_LABEL[WizardFieldOrigin.READ] }}
      </li>
      <li class="flex gap-2">
        <span class="size-1.5 shrink-0 mt-1.5 mx-0.5 rounded-full bg-warning" />
        {{ FIELD_ORIGIN_LABEL[WizardFieldOrigin.REQUIRED] }}
      </li>
      <li class="flex gap-2">
        <CircleHelp class="size-3 shrink-0 mt-0.5" />
        {{ FIELD_ORIGIN_LABEL[WizardFieldOrigin.DEFAULT] }}
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { Check, CircleHelp, ListChecks, Minus, Sparkles } from '@lucide/vue'

import {
  DSStepper,
  DSStepperItem,
  DSStepperTitle,
  DSStepperTrigger,
} from '@ds'

import { WizardFieldOrigin, WizardStep, WizardStepState } from '../utils/types'

import { FIELD_ORIGIN_LABEL, SUMMARY_STEP_KEY } from '../utils/const'

const { steps, activeKey } = defineProps<{
  steps: WizardStep[]
  activeKey: string
  hasSentence: boolean
}>()

const emit = defineEmits<{
  (e: 'select', key: string): void
}>()

/*
 * Reka counts steps from one; the wizard identifies them by key. The mapping
 * lives here rather than leaking step numbers into the wizard, where inserting
 * a step would then silently renumber everything downstream.
 */
const activeStep = computed(() => {
  const index = steps.findIndex(step => step.key === activeKey)
  return index === -1 ? 1 : index + 1
})

const handleSelect = (step: number | undefined) => {
  const target = steps[(step ?? 1) - 1]
  if (!target) return

  emit('select', target.key)
}
</script>
